/**
 * core/api/queries.ts — All monday.com GraphQL read operations.
 *
 * Each function is a thin async wrapper that calls `gqlClient` with a typed
 * query and returns normalised domain objects.  They are intended to be used
 * as `queryFn` callbacks inside TanStack React Query hooks.
 */

import { gqlClient } from './client';
import type {
  MondayItem,
  MondayBoard,
  MondayNotification,
  MondayUser,
  BoardHealthMetrics,
  SearchResult,
  Action,
  Note,
  Meeting,
  Topic,
} from '@core/types';

// ─── Me ───────────────────────────────────────────────────────────────────────

const GET_ME = `
  query GetMe {
    me {
      id
      name
      email
      photo_thumb_small
      title
    }
  }
`;

export async function queryMe(): Promise<MondayUser> {
  const data = await gqlClient<{ me: MondayUser }>(GET_ME);
  return data.me;
}

// ─── Boards ───────────────────────────────────────────────────────────────────

const GET_ALL_BOARDS = `
  query GetAllBoards($limit: Int) {
    boards(limit: $limit, order_by: created_at) {
      id
      name
      description
      board_kind
      state
    }
  }
`;

export async function queryAllBoards(limit = 50): Promise<MondayBoard[]> {
  const data = await gqlClient<{ boards: MondayBoard[] }>(GET_ALL_BOARDS, {
    limit,
  });
  return data.boards ?? [];
}

const GET_BOARD_ITEMS = `
  query GetBoardItems($boardId: ID!, $limit: Int) {
    boards(ids: [$boardId]) {
      items_page(limit: $limit) {
        items {
          id
          name
          created_at
          updated_at
          group { id title }
          column_values {
            id
            text
            value
            column { title type }
          }
        }
      }
    }
  }
`;

export async function queryBoardItems(
  boardId: string,
  limit = 100,
): Promise<MondayItem[]> {
  const data = await gqlClient<{
    boards: Array<{ items_page: { items: MondayItem[] } }>;
  }>(GET_BOARD_ITEMS, { boardId, limit });
  return data.boards?.[0]?.items_page?.items ?? [];
}

// ─── Tasks (My Work) ─────────────────────────────────────────────────────────

const GET_MY_TASKS = `
  query GetMyTasks($limit: Int) {
    me {
      id
    }
    boards(limit: 100) {
      id
      name
      items_page(
        limit: $limit
        query_params: {
          rules: [{ column_id: "person", compare_value: ["{me}"] }]
        }
      ) {
        items {
          id
          name
          board { id name }
          group { id title }
          created_at
          updated_at
          column_values {
            id
            text
            value
            column { title type }
          }
        }
      }
    }
  }
`;

export async function queryMyTasks(limit = 50): Promise<Action[]> {
  const data = await gqlClient<{
    boards: Array<{ id: string; name: string; items_page: { items: MondayItem[] } }>;
  }>(GET_MY_TASKS, { limit });

  // Flatten all boards' items into a single array and map to Action
  return (data.boards ?? [])
    .flatMap((b) => b.items_page?.items ?? [])
    .map(mapItemToAction);
}

// ─── Updates (Inbox) ─────────────────────────────────────────────────────────
//
// monday.com API does not expose a `notifications` field on the User type.
// We use the root `updates` query instead, which returns the most recent
// item updates (comments, status changes) across all boards.

const GET_UPDATES = `
  query GetUpdates($limit: Int!) {
    updates(limit: $limit) {
      id
      text_body
      created_at
      creator {
        id
        name
        photo_thumb_small
      }
      item_id
    }
  }
`;

interface RawUpdate {
  id: string;
  text_body: string;
  created_at: string;
  creator: { id: string; name: string; photo_thumb_small?: string };
  item_id?: string;
}

export async function queryInbox(limit = 50): Promise<MondayNotification[]> {
  const data = await gqlClient<{ updates: RawUpdate[] }>(GET_UPDATES, { limit });

  return (data.updates ?? []).map((u) => ({
    id: u.id,
    text: u.text_body || '(no content)',
    created_at: u.created_at,
    updatedAt: u.created_at,
    item_id: u.item_id ?? undefined,
    creator: u.creator,
  }));
}

// ─── Board Health (Monitor) ───────────────────────────────────────────────────

const GET_BOARD_HEALTH = `
  query GetBoardHealth($boardId: ID!) {
    boards(ids: [$boardId]) {
      id
      name
      items_page(limit: 500) {
        items {
          id
          name
          column_values {
            id
            text
            value
            column { title type }
          }
        }
      }
    }
  }
`;

/**
 * Fetch raw board data and compute BoardHealthMetrics.
 *
 * Status column: identified by column type `color` (monday's Status column).
 * Due date column: identified by column type `date`.
 *
 * healthScore formula (0–100):
 *   base = completionRate * 60
 *   penalty = (overdueCount / totalItems) * 25 + (blockedCount / totalItems) * 15
 *   healthScore = clamp(base + (40 - penalty * 40), 0, 100)
 * Simplified to: healthScore = clamp(round(completionRate * 60 + max(0, 40 - overdueRatio * 25 - blockedRatio * 15)), 0, 100)
 */
export async function queryBoardHealth(
  boardId: string,
): Promise<BoardHealthMetrics> {
  const data = await gqlClient<{
    boards: Array<{
      id: string;
      name: string;
      items_page: { items: MondayItem[] };
    }>;
  }>(GET_BOARD_HEALTH, { boardId });

  const board = data.boards?.[0];
  if (!board) {
    return {
      boardId,
      boardName: 'Unknown',
      totalItems: 0,
      doneCount: 0,
      inProgressCount: 0,
      overdueCount: 0,
      blockedCount: 0,
      healthScore: 0,
      insights: ['No data available for this board.'],
      computedAt: new Date().toISOString(),
    };
  }

  const items = board.items_page?.items ?? [];
  const today = new Date().toISOString().split('T')[0];

  let doneCount = 0;
  let inProgressCount = 0;
  let overdueCount = 0;
  let blockedCount = 0;
  let noDueDateCount = 0;
  const overdueItems: Array<{ id: string; name: string; dueDate?: string }> = [];

  for (const item of items) {
    const statusCol = item.column_values?.find(
      (cv) => cv.column.type === 'color',
    );
    const dueDateCol = item.column_values?.find(
      (cv) => cv.column.type === 'date',
    );

    const statusText = statusCol?.text?.toLowerCase() ?? '';
    const dueDate = dueDateCol?.text ?? '';

    if (statusText === 'done') {
      doneCount++;
    } else if (statusText === 'in progress' || statusText === 'working on it') {
      inProgressCount++;
    }
    if (statusText === 'blocked' || statusText === 'stuck') {
      blockedCount++;
    }
    if (!dueDate && statusText !== 'done') {
      noDueDateCount++;
    }
    if (dueDate && dueDate < today && statusText !== 'done') {
      overdueCount++;
      overdueItems.push({ id: item.id, name: item.name, dueDate });
    }
  }

  const totalItems = items.length;
  const completionRate = totalItems > 0 ? doneCount / totalItems : 0;
  const overdueRatio = totalItems > 0 ? overdueCount / totalItems : 0;
  const blockedRatio = totalItems > 0 ? blockedCount / totalItems : 0;
  const healthScore = Math.min(
    100,
    Math.max(
      0,
      Math.round(completionRate * 60 + Math.max(0, 40 - overdueRatio * 25 - blockedRatio * 15)),
    ),
  );

  // Build human-readable insights
  const insights: string[] = [];
  if (overdueCount > 0) {
    insights.push(`${overdueCount} item${overdueCount > 1 ? 's' : ''} overdue`);
  }
  if (blockedCount > 0) {
    insights.push(`${blockedCount} item${blockedCount > 1 ? 's' : ''} blocked or stuck`);
  }
  if (completionRate >= 0.8) {
    insights.push(`${Math.round(completionRate * 100)}% of items done — great progress`);
  } else if (completionRate >= 0.5) {
    insights.push(`${Math.round(completionRate * 100)}% of items done`);
  } else if (totalItems > 0) {
    insights.push(`${Math.round(completionRate * 100)}% completion — room to close out items`);
  }
  if (insights.length === 0) {
    insights.push('Board looks healthy');
  }

  return {
    boardId: board.id,
    boardName: board.name,
    totalItems,
    doneCount,
    inProgressCount,
    overdueCount,
    blockedCount,
    healthScore,
    noDueDateCount,
    insights,
    overdueItems,
    totalCount: totalItems,
    computedAt: new Date().toISOString(),
  };
}

// ─── Cross-board Search (Command Palette) ─────────────────────────────────────

const SEARCH_ITEMS = `
  query SearchItems($term: String!, $limit: Int) {
    boards(limit: 20) {
      id
      name
      items_page(
        limit: $limit
        query_params: {
          rules: [{
            column_id: "name"
            compare_value: [$term]
            operator: contains_text
          }]
        }
      ) {
        items {
          id
          name
          board { id name }
        }
      }
    }
  }
`;

export async function queryCrossBoardSearch(
  term: string,
  limit = 20,
): Promise<SearchResult[]> {
  try {
    const data = await gqlClient<{
      boards: Array<{
        id: string;
        name: string;
        items_page: { items: Array<{ id: string; name: string; board?: { id: string; name: string } }> };
      }>;
    }>(SEARCH_ITEMS, { term, limit });

    const results: SearchResult[] = [];

    for (const board of data.boards ?? []) {
      for (const item of board.items_page?.items ?? []) {
        results.push({
          id: item.id,
          type: 'item',
          name: item.name,
          subtitle: board.name,
          boardId: board.id,
          boardName: board.name,
        });
      }
    }

    return results;
  } catch {
    // Search failure is non-fatal — return empty array
    return [];
  }
}

// ── MondayItem → Domain type mappers ──────────────────────────────────────

/**
 * Build a lookup map from a MondayItem's column_values.
 *
 * Triple-indexes each column for resilient matching:
 *   1. Raw column ID  (e.g. "long_text_mm1abre3")
 *   2. Normalized column title  (e.g. "Due Date" → "due_date")
 *   3. Column type  (e.g. "long_text", "date", "color") — first-of-type wins
 *
 * This allows mappers to use logical names like cv['priority'], cv['due_date'],
 * cv['content'] regardless of the actual column ID format.
 */
export function columnMap(item: MondayItem): Record<string, string> {
  const cv: Record<string, string> = {};
  for (const c of item.column_values ?? []) {
    const text = c.text ?? '';

    // 1. Always index by raw column ID
    cv[c.id] = text;

    // 2. Index by normalized title (lowercase, spaces → underscores)
    if (c.column?.title) {
      const normalised = c.column.title
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
      if (!cv[normalised]) {
        cv[normalised] = text;
      }
    }

    // 3. Index by column type (first-of-type wins)
    if (c.column?.type && !cv[c.column.type]) {
      cv[c.column.type] = text;
    }
  }
  return cv;
}

function mapItemToAction(item: MondayItem): Action {
  const cv = columnMap(item);
  const escalatedRaw = cv['escalated'] ?? cv['checkbox'] ?? '';
  return {
    id: item.id,
    name: item.name,
    boardId: item.board?.id ?? item.boardId,
    boardName: item.board?.name ?? item.boardName,
    priority: (cv['priority'] || cv['status'] || undefined) as Action['priority'],
    status: cv['status4'] || cv['status'] || cv['color'] || undefined,
    dueDate: cv['date4'] || cv['due_date'] || cv['date'] || undefined,
    description: cv['long_text'] || cv['context'] || cv['description'] || cv['text'] || undefined,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    columnValues: cv,
    mondayItemId: item.id,
    completedDate: cv['completed_date'] || undefined,
    escalated: escalatedRaw === 'true' || escalatedRaw === 'Yes' || false,
  };
}

function mapItemToNote(item: MondayItem): Note {
  const cv = columnMap(item);
  return {
    id: item.id,
    name: item.name,
    content: cv['long_text'] || cv['content'] || undefined,
    type: cv['status'] || cv['type'] || cv['dropdown'] || 'General',
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

function mapItemToMeeting(item: MondayItem): Meeting {
  const cv = columnMap(item);
  const attendeeStr = cv['text'] || cv['attendees'] || '';
  return {
    id: item.id,
    name: item.name,
    date: cv['date4'] || cv['date'] || undefined,
    notes: cv['long_text'] || cv['agenda'] || cv['summary'] || undefined,
    attendees: attendeeStr
      ? attendeeStr.split(',').map((s) => s.trim()).filter(Boolean)
      : undefined,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

function mapItemToTopic(item: MondayItem): Topic {
  const cv = columnMap(item);
  return {
    id: item.id,
    name: item.name,
    color: cv['color_picker'] || cv['color'] || undefined,
    description: cv['long_text'] || cv['description'] || undefined,
    createdAt: item.created_at,
  };
}

// ── Additional aliases and helpers ─────────────────────────────────────────

/** Alias kept for backward compatibility with Inbox widget imports */
export const queryInboxNotifications = queryInbox;

export async function queryMeetings(boardId: string): Promise<Meeting[]> {
  const items = await queryBoardItems(boardId);
  return items.map(mapItemToMeeting);
}

export async function queryTopics(boardId: string): Promise<Topic[]> {
  const items = await queryBoardItems(boardId);
  return items.map(mapItemToTopic);
}

export async function queryNotes(boardId: string): Promise<Note[]> {
  const items = await queryBoardItems(boardId);
  return items.map(mapItemToNote);
}

export async function queryActions(boardId: string): Promise<Action[]> {
  const items = await queryBoardItems(boardId);
  return items.map(mapItemToAction);
}

export async function queryNotesByTopic(boardId: string, _topicId: string): Promise<Note[]> {
  const items = await queryBoardItems(boardId);
  return items.map(mapItemToNote);
}
